import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import {
  login as apiLogin,
  logout as apiLogout,
  checkAuth as apiCheckAuth,
  confirmMfaSetup as apiConfirmMfaSetup,
  verifyMfaLogin as apiVerifyMfaLogin,
  type User as ApiUser,
  type LoginResult,
} from '../services/authService';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  isPrismeAdmin: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  confirmMfaSetup: (setupToken: string, code: string) => Promise<LoginResult>;
  verifyMfaLogin: (mfaToken: string, code: string) => Promise<LoginResult>;
  applyAuthenticatedUser: (apiUser: ApiUser) => void;
  logout: () => Promise<void>;
  isLoading: boolean;
}

interface User {
  email: string;
  name: string;
  role: string;
  photo_url?: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function convertApiUserToUser(apiUser: ApiUser | null): User | null {
  if (!apiUser) return null;
  return {
    email: apiUser.email,
    name: `${apiUser.prenom} ${apiUser.nom}`,
    role: apiUser.titre_poste || 'Utilisateur PRIsME',
    photo_url: apiUser.photo_url || null,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isPrismeAdmin, setIsPrismeAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const applyAuthenticatedUser = (apiUser: ApiUser) => {
    setIsAuthenticated(true);
    setUser(convertApiUserToUser(apiUser));
    setIsPrismeAdmin(apiUser.acces_prisme_master === true);
  };

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        const apiUser = await apiCheckAuth();
        if (apiUser) {
          applyAuthenticatedUser(apiUser);
        } else {
          setIsAuthenticated(false);
          setUser(null);
          setIsPrismeAdmin(false);
        }
      } catch {
        setIsAuthenticated(false);
        setUser(null);
        setIsPrismeAdmin(false);
      } finally {
        setIsLoading(false);
      }
    };
    verifyAuth();
  }, []);

  const login = async (email: string, password: string): Promise<LoginResult> => {
    const result = await apiLogin(email, password);
    if (result.status === 'authenticated') {
      applyAuthenticatedUser(result.user);
    }
    return result;
  };

  const confirmMfaSetup = async (setupToken: string, code: string): Promise<LoginResult> => {
    const result = await apiConfirmMfaSetup(setupToken, code);
    if (result.status === 'authenticated') {
      applyAuthenticatedUser(result.user);
    }
    return result;
  };

  const verifyMfaLogin = async (mfaToken: string, code: string): Promise<LoginResult> => {
    const result = await apiVerifyMfaLogin(mfaToken, code);
    if (result.status === 'authenticated') {
      applyAuthenticatedUser(result.user);
    }
    return result;
  };

  const logout = async (): Promise<void> => {
    try {
      await apiLogout();
    } finally {
      setIsAuthenticated(false);
      setUser(null);
      setIsPrismeAdmin(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        isPrismeAdmin,
        login,
        confirmMfaSetup,
        verifyMfaLogin,
        applyAuthenticatedUser,
        logout,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
}
